import { cloneDeep } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, reactive, toRefs } from "vue";

import UserSettingsService from "@/common/api/user-settings.service";
import { getPrioritizedAvailableFromMethodIds } from "@/common/language-group-helpers";
import { LEVEL_EXPERT, LEVEL_INTERMEDIATE } from "@/common/level-types";
import { PACKAGE_GROUPS_PRIORITIZED } from "@/common/package-groups";
import type { UserPreferences } from "@/common/types/auth";
import { DEFAULT_FONT, DEFAULT_THEME } from "@/theming";
import type { Dictionary, Level, PackageGroup } from "@/types/dictionary";
import type { Font, LanguageGroup, PackageGroupId, Theme } from "@/types/site";

import { useFeatureFlagsStore } from ".";

interface State {
  activeLanguageGroup: LanguageGroup | undefined;
  dictionaries: Dictionary[];
  packageGroups: PackageGroup[];
  userPreferences: UserPreferences;
}

export const useSettingsStore = defineStore(
  "settings",
  () => {
    const state = reactive<State>({
      activeLanguageGroup: undefined,
      dictionaries: [],
      packageGroups: [],
      userPreferences: {
        articleFont: DEFAULT_FONT,
        onboardingShown: false,
        ordbogenArticleTranslationsExpandedDefault: true,
        gyldendalArticleTranslationsExpandedDefault: true,
        ordbogenWordclassLevel: LEVEL_EXPERT,
        theme: DEFAULT_THEME,
        allowHelpTools: true
      }
    });

    const prioritizedPackageGroups = computed<PackageGroup[]>(() => {
      const packageGroups = cloneDeep(state.packageGroups);

      return packageGroups.sort(
        (a, b) =>
          PACKAGE_GROUPS_PRIORITIZED.indexOf(a.id) - PACKAGE_GROUPS_PRIORITIZED.indexOf(b.id)
      );
    });

    const preferredPackageGroup = computed<PackageGroup | undefined>(() =>
      state.packageGroups.find((group) => group.isActive)
    );

    const preferredPackageGroupDictionaries = computed<Dictionary[]>(() => {
      const activePackageGroup = preferredPackageGroup.value?.id;
      if (!activePackageGroup) {
        return [];
      }

      return state.dictionaries.filter((dictionary) =>
        dictionary.packageGroups.includes(activePackageGroup)
      );
    });

    const prioritizedLanguageGroups = computed(() => {
      const methodIds = preferredPackageGroupDictionaries.value.map(
        (dictionary) => dictionary.methodId
      );

      return getPrioritizedAvailableFromMethodIds(methodIds);
    });

    /**
     * Word class level across packages.
     * Currently Gyldendal doesn't have it's own setting, so we default to intermediate.
     */
    const activeWordClassLevel = computed<Level>(() => {
      if (preferredPackageGroup.value?.id === "gyldendal") {
        return LEVEL_INTERMEDIATE;
      }

      return state.userPreferences.ordbogenWordclassLevel;
    });

    const setSettings = (settings: Partial<State>) => {
      Object.assign(state, { ...settings });
    };

    /**
     * @throws {Error} When invalid language group is selected
     */
    const setActiveLanguageGroup = (languageGroup: LanguageGroup | undefined) => {
      if (!preferredPackageGroup.value) {
        return;
      }

      const isValid =
        languageGroup === undefined ||
        preferredPackageGroup.value.languages.includes(languageGroup);
      if (!isValid) {
        throw new Error(`Invalid language group selected: "${languageGroup}"`);
      }

      state.activeLanguageGroup = languageGroup;
    };

    const setGyldenalArticleTranslationsExpandedDefault = async (value: boolean) => {
      await UserSettingsService.updateGyldendalArticleTranslationsExpandedDefault(value);

      state.userPreferences.gyldendalArticleTranslationsExpandedDefault = value;
    };

    const setOrdbogenArticleTranslationsExpandedDefault = async (value: boolean) => {
      await UserSettingsService.updateOrdbogenArticleTranslationsExpandedDefault(value);

      state.userPreferences.ordbogenArticleTranslationsExpandedDefault = value;
    };

    const setOrdbogenWordClassLevel = async (level: Level) => {
      await UserSettingsService.updateOrdbogenWordclassLevel(level);

      state.userPreferences.ordbogenWordclassLevel = level;
    };

    const setArticleFont = async (font: Font) => {
      await UserSettingsService.updateFont(font);

      state.userPreferences.articleFont = font;
    };

    const setTheme = async (theme: Theme) => {
      await UserSettingsService.updateTheme(theme);

      state.userPreferences.theme = theme;
    };

    const setAllowHelpTools = async (value: boolean) => {
      await UserSettingsService.setAllowHelpTools(value);

      // Refresh feature flags via backend to ensure server settings are respected
      const { refreshFeatureFlags } = useFeatureFlagsStore();
      refreshFeatureFlags();

      state.userPreferences.allowHelpTools = value;
    };

    const finishOnboarding = () => {
      // Avoid waiting for a response, assuming it was a success
      UserSettingsService.onboardingShown();
      state.userPreferences.onboardingShown = true;
    };

    /**
     * @throws {Error} When invalid package group is selected
     */
    const updateActivePackageGroup = (packageGroupId: string) => {
      const isValid = prioritizedPackageGroups.value.some(
        (packageGroup) => packageGroup.id === packageGroupId
      );

      if (!isValid) {
        throw new Error(`Invalid package group selected: "${packageGroupId}"`);
      }

      // Avoid waiting for a response, assuming it was a success
      UserSettingsService.updatePackageGroup(packageGroupId);

      const packageGroups = prioritizedPackageGroups.value.map((packageGroup) => ({
        ...packageGroup,

        id: packageGroup.id,
        isActive: packageGroup.id === packageGroupId
      }));

      state.packageGroups = packageGroups;
    };

    const setPackageGroupLanguageLevel = (
      packageGroup: PackageGroupId,
      language: LanguageGroup,
      level: Level
    ) => {
      const packageGroupIndex = state.packageGroups.findIndex((p) => p.id === packageGroup);
      if (packageGroupIndex === -1) {
        return;
      }

      const languageLevelIndex = state.packageGroups[packageGroupIndex].languageLevels.findIndex(
        (l) => l.language === language
      );
      if (languageLevelIndex === -1) {
        return;
      }

      state.packageGroups[packageGroupIndex].languageLevels.splice(languageLevelIndex, 1, {
        language: language,
        level: level
      });
    };

    const setPackageGroupLevels = (packageGroup: PackageGroupId, level: Level) => {
      const packageGroupIndex = state.packageGroups.findIndex((p) => p.id === packageGroup);
      if (packageGroupIndex === -1) {
        return;
      }

      state.packageGroups[packageGroupIndex].languageLevels.forEach((languageLevel) => {
        languageLevel.level = level;
      });
    };

    return {
      ...toRefs(state),

      prioritizedPackageGroups,
      preferredPackageGroup,
      preferredPackageGroupDictionaries,
      prioritizedLanguageGroups,
      activeWordClassLevel,

      finishOnboarding,
      setActiveLanguageGroup,
      setArticleFont,
      setGyldenalArticleTranslationsExpandedDefault,
      setOrdbogenArticleTranslationsExpandedDefault,
      setOrdbogenWordClassLevel,
      setPackageGroupLanguageLevel,
      setPackageGroupLevels,
      setSettings,
      setTheme,
      setAllowHelpTools,
      updateActivePackageGroup
    };
  },
  {
    persist: {
      paths: ["userPreferences.theme", "activeLanguageGroup"]
    }
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSettingsStore, import.meta.hot));
}
